<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useReferenceSelectionStore } from '../../stores/referenceSelectionStore';
import TagMenu from './TagMenu.vue';

const { t } = useI18n();

/**
 * Floating bulk-actions bar for the references panel.
 *
 * Appears at the bottom-right of the references panel while one or more
 * table rows are checked. Hosts the shared TagMenu (session-only tagging
 * POC — see referenceSelectionStore.ts) and a disabled "Save to narrative"
 * placeholder pending backend support.
 */

const selectionStore = useReferenceSelectionStore();

const isTagMenuOpen = ref(false);
const rootEl = ref<HTMLElement | null>(null);

const selectionLabel = computed(() => {
    const count = selectionStore.selectionCount;
    return t('references.selectedCount', { count: count > 999 ? '999+' : count });
});

const selectedHashList = computed(() => [...selectionStore.selectedHashes]);

const toggleTagMenu = () => {
    isTagMenuOpen.value = !isTagMenuOpen.value;
};

// Hide the menu when the selection is emptied out from under it (e.g. the
// header checkbox was untoggled).
watch(
    () => selectionStore.hasSelection,
    (has) => {
        if (!has) isTagMenuOpen.value = false;
    },
);

const onClearSelection = () => {
    selectionStore.clearSelection();
};

const onWindowClick = (event: MouseEvent) => {
    if (!isTagMenuOpen.value) return;
    if (rootEl.value && !rootEl.value.contains(event.target as Node)) {
        isTagMenuOpen.value = false;
    }
};

const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        isTagMenuOpen.value = false;
    }
};

onMounted(() => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('click', onWindowClick);
    window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
    <div v-if="selectionStore.hasSelection" ref="rootEl" class="bulk-actions-anchor">
        <TagMenu
            v-if="isTagMenuOpen"
            :content-hashes="selectedHashList"
            :anchor-el="rootEl"
            placement="up"
            @close="isTagMenuOpen = false"
        />

        <div class="bulk-actions-bar">
            <span class="bulk-actions-count">{{ selectionLabel }}</span>
            <button
                type="button"
                class="bulk-actions-button"
                :class="{ 'bulk-actions-button-active': isTagMenuOpen }"
                @click="toggleTagMenu"
            >
                <span class="material-icons-outlined">sell</span>
                {{ t('references.addTag') }}
            </button>
            <button
                type="button"
                class="bulk-actions-button"
                disabled
                :title="t('references.comingSoon')"
            >
                <span class="material-icons-outlined">bookmark_add</span>
                {{ t('references.saveToNarrative') }}
            </button>
            <button
                type="button"
                class="bulk-actions-button bulk-actions-close"
                :title="t('references.clearSelection')"
                @click="onClearSelection"
            >
                <span class="material-icons">close</span>
            </button>
        </div>
    </div>
</template>
